import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth'
import { auth, googleProvider } from '../lib/firebase'
import { apiGet } from '../lib/api'
import { qk, queryClient } from '../lib/queryClient'
import { limpiarCachePersistido } from '../lib/persist'
import { escritorio } from '../lib/desktop'
import type { Me, PermKey } from '../lib/types'

// Contrato de useAuth() — lo consumen chrome y páginas:
//   { user, me, loading, login, logout, can }
// user = sesión Firebase mínima; me = identidad + permisos resueltos por la API (/api/me).

export interface AuthUser {
  email: string
  name: string
  photoUrl?: string
  role: string
}

interface AuthValue {
  user: AuthUser | null
  me: Me | null
  loading: boolean
  login: () => Promise<void>
  logout: () => void
  can: (key: PermKey) => boolean
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [fbUser, setFbUser] = useState<User | null>(null)
  const [authListo, setAuthListo] = useState(false)

  useEffect(() => {
    return onIdTokenChanged(auth, (u) => {
      setFbUser(u)
      setAuthListo(true)
    })
  }, [])

  // /api/me por react-query (antes iba con apiGet crudo y un useState). El cambio
  // importa por OFFLINE: al fallar el fetch, `me` quedaba en null y can() devolvía
  // false para TODO — la app abría pero sin botón de crear, con /admin rebotando y
  // los gates colapsados; o sea, parecía rota. Ahora la respuesta se sirve del cache
  // persistido (qk.me está en la allowlist de lib/persist.ts) y los permisos
  // sobreviven a quedarse sin red.
  const { data: me = null, isLoading: cargandoMe } = useQuery({
    queryKey: qk.me,
    queryFn: () => apiGet<Me>('/api/me'),
    enabled: !!fbUser,
    // La identidad cambia poco y es lo primero que se necesita: se sirve del cache
    // al instante y se revalida en fondo.
    staleTime: 5 * 60 * 1000,
  })

  const loading = !authListo || (!!fbUser && cargandoMe)

  const value = useMemo<AuthValue>(
    () => ({
      // La sesión la dicta FIREBASE, nunca el caché. `me` viene hidratado de
      // IndexedDB y puede estar presente sin usuario de Firebase (recarga con la
      // sesión aún restaurándose, o ya cerrada): tomarlo como sesión pintaba la
      // app, disparaba peticiones sin token y encadenaba un bucle de 401 →
      // /bienvenida → home. Con usuario de Firebase, `me` sigue aportando
      // nombre, foto, rol y permisos (también sin red).
      user: !fbUser?.email
        ? null
        : me
          ? { email: me.email, name: me.name, photoUrl: me.photoUrl ?? undefined, role: me.role }
          : { email: fbUser.email.toLowerCase(), name: fbUser.displayName ?? fbUser.email, role: '…' },
      me,
      loading,
      login: async () => {
        // ESCRITORIO: Google prohíbe OAuth en ventanas embebidas
        // (disallowed_useragent) y ya no acepta esquemas custom, así que el
        // cascarón abre el NAVEGADOR DEL SISTEMA con PKCE y recibe el código en
        // un loopback. Lo que vuelve es el id_token DE GOOGLE, no una sesión de
        // Firebase: la sesión se crea aquí, en el renderer, para que el resto de
        // la app (auth.currentUser, apiFetch, reglas de Firestore) siga viviendo
        // en el mismo sitio que en el navegador.
        // El rechazo se deja PASAR TAL CUAL: trae { code } y Bienvenida lo
        // traduce con mensajeLoginEscritorio.
        const d = escritorio()
        if (d) {
          const { idToken } = await d.login()
          await signInWithCredential(auth, GoogleAuthProvider.credential(idToken))
          return
        }
        // Popup con fallback a redirect (patrón Cells): algunos navegadores/paneles
        // bloquean popups; el redirect siempre funciona en la misma pestaña.
        try {
          await signInWithPopup(auth, googleProvider)
        } catch (err) {
          const code = (err as { code?: string }).code
          if (
            code === 'auth/popup-blocked' ||
            code === 'auth/cancelled-popup-request' ||
            code === 'auth/operation-not-supported-in-this-environment'
          ) {
            await signInWithRedirect(auth, googleProvider)
            return
          }
          throw err
        }
      },
      logout: () => {
        void signOut(auth)
        // Los datos de bee no se quedan en el disco de nadie: se tira el cache en
        // memoria Y el persistido en IndexedDB.
        queryClient.clear()
        void limpiarCachePersistido()
      },
      can: (key) => me?.perms[key] ?? false,
    }),
    [fbUser, me, loading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth fuera de <AuthProvider>')
  return ctx
}
